import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Documentary,
  DocumentariesQueryParams,
  DocumentariesData,
  DocumentaryCreateRequest,
  DocumentaryUpdateRequest,
  DocumentaryDetailsResponse,
} from '../../types/api/documentary';
import type {
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  CompleteMultipartUploadRequest,
  VideoVariant,
  VideoMultipartStatusResponse,
  VideoMultipartRegenerateRequest,
  VideoMultipartPresignResponse,
} from '../../types/api/movie';

/**
 * Documentary Service
 *
 * Handles all documentary-related API operations for admin functionality.
 *
 * Features:
 * - Documentary listing with pagination and filtering
 * - Documentary CRUD operations (create, update, soft delete, permanent delete)
 * - Form-data upload support for thumbnails
 * - Separate thumbnail-only update endpoint
 */
export class DocumentaryService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Get documentaries list with pagination and filters.
   *
   * Backend expects 1-based page index.
   */
  async getDocumentaries(params: DocumentariesQueryParams = {}): Promise<DocumentariesData> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }
    if (params.languageId) {
      queryParams.append('languageId', params.languageId);
    }
    if (params.documentaryType) {
      queryParams.append('documentaryType', params.documentaryType);
    }
    if (params.documentarySubtype) {
      queryParams.append('documentarySubtype', params.documentarySubtype);
    }
    if (params.accessLevel) {
      queryParams.append('accessLevel', params.accessLevel);
    }
    if (params.contentRegion) {
      queryParams.append('contentRegion', params.contentRegion);
    }

    const url = `${API_ENDPOINTS.DOCUMENTARIES.ADMIN_LIST}?${queryParams.toString()}`;
    const response = await this.httpClient.get<DocumentariesData>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch documentaries');
    }

    return response.data;
  }

  /**
   * Get documentary by ID with genres, casts, and media details.
   * Uses public endpoint: /public/documentaries/:documentaryId
   */
  async getDocumentary(documentaryId: string): Promise<DocumentaryDetailsResponse> {
    const url = API_ENDPOINTS.DOCUMENTARIES.PUBLIC_DETAILS(documentaryId);
    const response = await this.httpClient.get<DocumentaryDetailsResponse>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch documentary');
    }

    return response.data;
  }

  /**
   * Create a new documentary with optional thumbnail upload.
   *
   * Backend expects multipart/form-data with:
   * - data: JSON (DocumentaryCreateRequest) (required)
   * - thumbnail: file (optional)
   */
  async createDocumentary(
    payload: DocumentaryCreateRequest,
    thumbnailFile?: File
  ): Promise<Documentary> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }

    const response = await this.httpClient.request<Documentary>({
      method: 'POST',
      url: API_ENDPOINTS.DOCUMENTARIES.ADMIN_CREATE_MULTIPART,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create documentary');
    }

    return response.data;
  }

  /**
   * Create a new documentary using JSON-only endpoint (pre-uploaded thumbnail URL).
   */
  async createDocumentaryJson(payload: DocumentaryCreateRequest): Promise<Documentary> {
    const response = await this.httpClient.post<Documentary>(
      API_ENDPOINTS.DOCUMENTARIES.ADMIN_CREATE_JSON,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create documentary');
    }

    return response.data;
  }

  /**
   * Update an existing documentary.
   *
   * Uses JSON body and partial update semantics as per backend docs.
   */
  async updateDocumentary(
    documentaryId: string,
    payload: DocumentaryUpdateRequest
  ): Promise<Documentary> {
    const url = API_ENDPOINTS.DOCUMENTARIES.ADMIN_UPDATE(documentaryId);
    const response = await this.httpClient.put<Documentary>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update documentary');
    }

    return response.data;
  }

  /**
   * Soft delete a documentary.
   *
   * Marks status = INACTIVE and performs best-effort cleanup on video assets.
   */
  async softDeleteDocumentary(documentaryId: string): Promise<void> {
    const url = API_ENDPOINTS.DOCUMENTARIES.ADMIN_SOFT_DELETE(documentaryId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete documentary');
    }
  }

  /**
   * Permanently delete a documentary.
   *
   * SUPER_ADMIN only. Cascades through all related entities and storage keys.
   */
  async permanentDeleteDocumentary(documentaryId: string): Promise<void> {
    const url = API_ENDPOINTS.DOCUMENTARIES.ADMIN_PERMANENT_DELETE(documentaryId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to permanently delete documentary');
    }
  }

  /**
   * Replace documentary thumbnail using the dedicated thumbnail endpoint.
   *
   * Backend endpoint: PUT /api/admin/documentaries/{documentaryId}/thumbnail
   * Content-Type: multipart/form-data
   * Parts:
   *  - thumbnail: file (required)
   */
  async updateThumbnail(
    documentaryId: string,
    thumbnailFile: File
  ): Promise<Documentary> {
    const formData = new FormData();
    formData.append('thumbnail', thumbnailFile, thumbnailFile.name);

    const response = await this.httpClient.request<Documentary>({
      method: 'PUT',
      url: API_ENDPOINTS.DOCUMENTARIES.ADMIN_UPDATE_THUMBNAIL(documentaryId),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update documentary thumbnail');
    }

    return response.data;
  }

  /**
   * Initiate multipart video upload for a documentary.
   */
  async initiateMultipartVideoUpload(
    contentId: string,
    payload: InitiateMultipartUploadRequest
  ): Promise<InitiateMultipartUploadResponse> {
    const response = await this.httpClient.post<InitiateMultipartUploadResponse>(
      API_ENDPOINTS.DOCUMENTARIES.ADMIN_INITIATE_MULTIPART_UPLOAD(contentId),
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to initiate video upload');
    }

    return response.data;
  }

  /**
   * Complete multipart video upload for a documentary.
   */
  async completeMultipartVideoUpload(
    contentId: string,
    payload: CompleteMultipartUploadRequest
  ): Promise<VideoVariant> {
    const response = await this.httpClient.post<VideoVariant>(
      API_ENDPOINTS.DOCUMENTARIES.ADMIN_COMPLETE_MULTIPART_UPLOAD(contentId),
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to complete video upload');
    }

    return response.data;
  }

  /**
   * Get multipart upload status for a documentary video.
   */
  async getMultipartUploadStatus(
    contentId: string,
    key: string,
    uploadId: string
  ): Promise<VideoMultipartStatusResponse> {
    const queryParams = new URLSearchParams({
      key,
      uploadId,
    });

    const response = await this.httpClient.get<VideoMultipartStatusResponse>(
      `${API_ENDPOINTS.DOCUMENTARIES.ADMIN_GET_MULTIPART_STATUS(contentId)}?${queryParams.toString()}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch multipart upload status');
    }

    return response.data;
  }

  /**
   * Regenerate presigned part URLs for a documentary video multipart upload.
   */
  async regenerateMultipartPartUrls(
    contentId: string,
    request: VideoMultipartRegenerateRequest
  ): Promise<VideoMultipartPresignResponse> {
    const response = await this.httpClient.post<VideoMultipartPresignResponse>(
      API_ENDPOINTS.DOCUMENTARIES.ADMIN_REGENERATE_PART_URLS(contentId),
      request
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to regenerate multipart part URLs');
    }

    return response.data;
  }

  /**
   * Abort multipart video upload for a documentary.
   */
  async abortMultipartVideoUpload(params: { key: string; uploadId: string }): Promise<void> {
    const queryParams = new URLSearchParams({
      key: params.key,
      uploadId: params.uploadId,
    });

    const response = await this.httpClient.delete<{ message: string }>(
      `${API_ENDPOINTS.DOCUMENTARIES.ADMIN_ABORT_MULTIPART_UPLOAD}?${queryParams.toString()}`
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to abort video upload');
    }
  }

  /**
   * Delete a video variant from a documentary.
   */
  async deleteVideo(contentId: string, fileId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      API_ENDPOINTS.DOCUMENTARIES.ADMIN_DELETE_VIDEO(contentId, fileId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete video');
    }
  }
}

